const toNonNegativeSeconds = (value: number): number =>
  Math.max(0, Math.trunc(value));

const parseNumericEndsAt = (endsAt: number): number =>
  Number.isFinite(endsAt) ? toNonNegativeSeconds(endsAt) : 0;

const parseStringEndsAt = (endsAt: string): number => {
  const trimmed = endsAt.trim();
  if (trimmed !== '' && /^\d+$/.test(trimmed)) {
    return parseNumericEndsAt(Number(trimmed));
  }
  const ms = Date.parse(endsAt);
  if (!Number.isNaN(ms)) {
    return toNonNegativeSeconds(Math.floor(ms / 1000));
  }
  return 0;
};

/**
 * Converts the Pause Internet `ends_at` (epoch seconds as a number, a numeric
 * string, or an ISO date string) into non-negative epoch seconds. Returns 0 for
 * missing or unparseable values. Single source of truth shared by the API
 * mapping in `fetchRules` and the store getter.
 */
export const parsePauseInternetEndsAtInSeconds = (
  endsAt: number | string | null | undefined
): number => {
  if (endsAt === undefined || endsAt === null) {
    return 0;
  }
  if (typeof endsAt === 'number') {
    return parseNumericEndsAt(endsAt);
  }
  if (typeof endsAt === 'string') {
    return parseStringEndsAt(endsAt);
  }
  return 0;
};

/**
 * Stored Pause Internet value. The current shape is `{ endsAtInSeconds }`, but
 * devices updated from 0.21.1 or earlier may still hold the raw API shape
 * (`{ ends_at }`) until the next rules sync rewrites it.
 */
type StoredPauseInternet = {
  endsAtInSeconds?: number;
  ends_at?: number | string;
};

/**
 * Resolves the pause end in epoch seconds from a persisted value that may be in
 * the current (`{ endsAtInSeconds }`) or the legacy (`{ ends_at }`) shape.
 * Returns 0 when there is no pause. Honors the store invariant that consumers
 * never see the raw `ends_at` shape.
 */
export const resolvePauseInternetEndsAtInSeconds = (
  pause: StoredPauseInternet | null | undefined
): number => {
  if (!pause) {
    return 0;
  }
  if (typeof pause.endsAtInSeconds === 'number') {
    return parsePauseInternetEndsAtInSeconds(pause.endsAtInSeconds);
  }
  return parsePauseInternetEndsAtInSeconds(pause.ends_at);
};
